import React, { useState } from 'react';
import {
  Box,
  Text,
  Button,
  HStack,
  Spinner,
  IconButton,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { environment } from '../config/environment';
import { useColorModeValue } from './ui/color-mode';
import { FaChevronDown, FaChevronUp, FaFileAlt } from 'react-icons/fa';
import 'highlight.js/styles/github.css';

// Add custom CSS for proper code block styling
const codeBlockCSS = `
  /* Ensure code elements inside pre blocks have transparent backgrounds */
  .markdown-content pre code {
    background: transparent !important;
    padding: 0 !important;
    border: none !important;
    border-radius: 0 !important;
  }
  
  /* Dark mode syntax highlighting */
  .markdown-content.dark-mode .hljs {
    background: transparent !important;
    color: #e6edf3 !important;
  }
  .markdown-content.dark-mode .hljs-keyword { color: #ff7b72 !important; }
  .markdown-content.dark-mode .hljs-string { color: #a5d6ff !important; }
  .markdown-content.dark-mode .hljs-comment { color: #8b949e !important; }
  .markdown-content.dark-mode .hljs-number { color: #79c0ff !important; }
  .markdown-content.dark-mode .hljs-function { color: #d2a8ff !important; }
  .markdown-content.dark-mode .hljs-variable { color: #ffa657 !important; }
  .markdown-content.dark-mode .hljs-type { color: #7ee787 !important; }
  .markdown-content.dark-mode .hljs-built_in { color: #ffa657 !important; }
  .markdown-content.dark-mode .hljs-literal { color: #79c0ff !important; }
  .markdown-content.dark-mode .hljs-attr { color: #79c0ff !important; }
  .markdown-content.dark-mode .hljs-tag { color: #7ee787 !important; }
  .markdown-content.dark-mode .hljs-name { color: #7ee787 !important; }
  
  /* Light mode - ensure code inside pre has transparent background */
  .markdown-content:not(.dark-mode) pre code {
    background: transparent !important;
  }
`;

// Inject code block styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('code-block-css');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'code-block-css';
    style.textContent = codeBlockCSS;
    document.head.appendChild(style);
  }
}

interface ReadmeViewerProps {
  namespace: string;
  moduleName: string;
  system: string;
  version: string;
}

export const ReadmeViewer: React.FC<ReadmeViewerProps> = ({
  namespace,
  moduleName,
  system,
  version,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const readmeBg = useColorModeValue('gray.50', 'gray.900');
  const isDarkMode = useColorModeValue(false, true);

  const { data: readmeContent, isLoading, error, refetch } = useQuery({
    queryKey: ['readme', namespace, moduleName, system, version],
    queryFn: async () => {
      const response = await axios.get(
        `${environment.apiBaseUrl}/v1/modules/${namespace}/${moduleName}/${system}/${version}/readme`,
        {
          headers: {
            'Accept': 'text/markdown',
          },
        }
      );
      return response.data;
    },
    enabled: isExpanded, // Only fetch when expanded
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Get theme values for consistent styling
  const inlineCodeBg = useColorModeValue('#f6f8fa', '#161b22');
  const inlineCodeColor = useColorModeValue('#24292f', '#e6edf3');
  const blockquoteBorderColor = useColorModeValue('#d0d7de', '#30363d');
  const blockquoteTextColor = useColorModeValue('#656d76', '#8b949e');
  const tableBorderColor = useColorModeValue('#d1d9e0', '#30363d');
  const tableHeaderBg = useColorModeValue('#f6f8fa', '#161b22');
  const codeBlockBg = useColorModeValue('#f6f8fa', '#0d1117');
  const linkColor = useColorModeValue('#0969da', '#58a6ff');

  // Proper markdown components with clean styling
  const markdownComponents = {
    h1: (props: any) => (
      <Text
        as="h1"
        fontSize="2xl"
        fontWeight="bold"
        mb={4}
        mt={6}
        pb={2}
        borderBottom="1px solid"
        borderColor={cardBorder}
        color={textColor}
      >
        {props.children}
      </Text>
    ),
    h2: (props: any) => (
      <Text
        as="h2"
        fontSize="xl"
        fontWeight="semibold"
        mb={3}
        mt={5}
        pb={2}
        borderBottom="1px solid"
        borderColor={cardBorder}
        color={textColor}
      >
        {props.children}
      </Text>
    ),
    h3: (props: any) => (
      <Text
        as="h3"
        fontSize="lg"
        fontWeight="semibold"
        mb={2}
        mt={4}
        color={textColor}
      >
        {props.children}
      </Text>
    ),
    h4: (props: any) => (
      <Text
        as="h4"
        fontSize="md"
        fontWeight="semibold"
        mb={2}
        mt={3}
        color={textColor}
      >
        {props.children}
      </Text>
    ),
    p: (props: any) => (
      <Text mb={4} lineHeight="1.6" color={textColor}>
        {props.children}
      </Text>
    ),
    a: (props: any) => (
      <Text
        as="a"
        {...props}
        color={linkColor}
        textDecoration="underline"
        _hover={{ textDecoration: 'none' }}
      />
    ),
    ul: (props: any) => (
      <Box as="ul" pl={6} mb={4} listStyleType="disc">
        {props.children}
      </Box>
    ),
    ol: (props: any) => (
      <Box as="ol" pl={6} mb={4} listStyleType="decimal">
        {props.children}
      </Box>
    ),
    li: (props: any) => (
      <Text as="li" mb={1} color={textColor}>
        {props.children}
      </Text>
    ),
    blockquote: (props: any) => (
      <Box
        as="blockquote"
        borderLeft="4px solid"
        borderColor={blockquoteBorderColor}
        pl={4}
        py={2}
        my={4}
        color={blockquoteTextColor}
        fontStyle="italic"
      >
        {props.children}
      </Box>
    ),
    table: (props: any) => (
      <Box overflowX="auto" mb={4}>
        <Box
          as="table"
          w="full"
          borderCollapse="collapse"
          border="1px solid"
          borderColor={tableBorderColor}
          borderRadius="md"
        >
          {props.children}
        </Box>
      </Box>
    ),
    thead: (props: any) => (
      <Box as="thead" bg={tableHeaderBg}>
        {props.children}
      </Box>
    ),
    th: (props: any) => (
      <Text
        as="th"
        p={3}
        textAlign="left"
        fontWeight="semibold"
        border="1px solid"
        borderColor={tableBorderColor}
        color={textColor}
      >
        {props.children}
      </Text>
    ),
    td: (props: any) => (
      <Text
        as="td"
        p={3}
        border="1px solid"
        borderColor={tableBorderColor}
        color={textColor}
      >
        {props.children}
      </Text>
    ),
    pre: (props: any) => (
      <Box
        as="pre"
        bg={codeBlockBg}
        border="1px solid"
        borderColor={cardBorder}
        borderRadius="md"
        p={4}
        mb={4}
        overflow="auto"
        fontSize="sm"
        fontFamily="ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace"
      >
        {props.children}
      </Box>
    ),
    code: (props: any) => {
      // Only style inline code (no language class), let pre handle code blocks
      if (!props.className || !props.className.startsWith('language-')) {
        return (
          <Text
            as="code"
            bg={inlineCodeBg}
            color={inlineCodeColor}
            px={1}
            py={0.5}
            borderRadius="sm"
            fontSize="85%"
            fontFamily="ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace"
          >
            {props.children}
          </Text>
        );
      }
      
      // For code blocks, ensure transparent background
      return (
        <code
          {...props}
          style={{
            backgroundColor: 'transparent',
            padding: 0,
            border: 'none',
            borderRadius: 0,
          }}
        />
      );
    },
  };

  return (
    <Box
      w="full"
      borderWidth="1px"
      borderColor={cardBorder}
      borderRadius="md"
      bg={cardBg}
      overflow="hidden"
    >
      <HStack
        p={3}
        justify="space-between"
        align="center"
        cursor="pointer"
        onClick={handleToggle}
        _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
        transition="background-color 0.2s"
      >
        <HStack>
          <FaFileAlt color={mutedTextColor} />
          <Text fontWeight="medium" color={textColor}>
            README
          </Text>
          {isLoading && <Spinner size="sm" />}
        </HStack>
        <IconButton
          aria-label={isExpanded ? "Collapse README" : "Expand README"}
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
        >
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </IconButton>
      </HStack>

      {isExpanded && (
        <Box p={4} pt={0}>
          {error && (
            <Box
              p={3}
              bg={useColorModeValue('orange.50', 'orange.900')}
              borderColor={useColorModeValue('orange.200', 'orange.700')}
              borderWidth="1px"
              borderRadius="md"
              mb={3}
            >
              <Text fontSize="sm" fontWeight="medium" color={useColorModeValue('orange.800', 'orange.200')} mb={1}>
                README not available
              </Text>
              <Text fontSize="sm" color={useColorModeValue('orange.700', 'orange.300')}>
                No README file found for this module version.
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  ml={2}
                  onClick={() => refetch()}
                  height="auto"
                  py={1}
                  px={2}
                  fontSize="xs"
                >
                  Retry
                </Button>
              </Text>
            </Box>
          )}

          {readmeContent && (
            <Box
              p={4}
              bg={readmeBg}
              borderRadius="md"
              borderWidth="1px"
              borderColor={cardBorder}
              maxH="500px"
              overflowY="auto"
              className={`markdown-content ${isDarkMode ? 'dark-mode' : ''}`}
              color={textColor}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight, rehypeRaw]}
                components={markdownComponents}
              >
                {readmeContent}
              </ReactMarkdown>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};